import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';

import { APIService } from '../api';
import { KPI } from '../api/models';
import { map, shareReplay } from 'rxjs/operators';
import { UtilsService } from './utils.service';


@Injectable()
export class DocumentsService {

  constructor(
    private api: APIService,
  ) { }

  /**
   * Load documents using params
   * @param params
   */
  private _documents: { [key: string]: Observable<KPI.Document[]> } = {};
  public loadDocumentsWithParams(params?: any): Observable<KPI.Document[]> {
    const key = UtilsService.getKey(arguments);
    if (!this._documents[key]) {
      this._documents[key] = this.api.documents_documents(params).pipe(
        shareReplay(1),
      );
    }
    return this._documents[key];
  }

  public clearDocumentsCache() {
    this._documents = {};
  }

  public _documentsHaveChanged$ = new BehaviorSubject<void>(undefined);
  public documentsHaveChanged() {
    this._documentsHaveChanged$.next();
  }

  public childDialogOpen = false;

  public getDocuments(
    assetsIds: number[],
    investIds: number[],
    actionIds: number[],
    perimeterIds: number[],
  ): Observable<KPI.Document[][]> {
    return combineLatest([
      assetsIds.length
        ? this.loadDocumentsWithParams({ assets: assetsIds.join(',') })
        : of([]),
      investIds.length
        ? this.loadDocumentsWithParams({ investments: investIds.join(',') })
        : of([]),
      actionIds.length
        ? this.loadDocumentsWithParams({ initiatives: actionIds.join(',') })
        : of([]),
      perimeterIds.length
        ? this.loadDocumentsWithParams({ perimeters: perimeterIds.join(',') })
        : of([]),
    ])
  }

  public getDocumentsCount(
    assetsIds: number[],
    investIds: number[],
    actionIds: number[],
    perimeterIds: number[],
  ): Observable<number> {
    return this.getDocuments(assetsIds, investIds, actionIds, perimeterIds).pipe(
      map(([assetsDocs, invDocs, actionsDoc, perimeterIds]) => {
        return assetsDocs.length + invDocs.length + actionsDoc.length + perimeterIds.length;
      })
    )
  }

  public addDocument(investmentId: number, params): Observable<KPI.Document> {
    return this.api.documents_add_investment_document(investmentId, params)
  }

  public deleteDocument(id: number): Observable<void> {
    this.clearDocumentsCache();
    return this.api.documents_delete(id);
  }
  
  public addPerimeterDocument(perimeterId: number, params): Observable<KPI.Document> {
    return this.api.documents_add_perimeter_document(perimeterId, params);
  }

  public addAssetDocument(assetId: number, params): Observable<KPI.Document> {
    return this.api.documents_add_asset_document(assetId, params);
  }

  public addInitiativeDocument(initiativeId: number, params): Observable<KPI.Document> {
    return this.api.documents_add_initiative_document(initiativeId, params);
  }

  public updateDocumentLinkedEquipment(docId: number, linkedEquipmentIds: number[]): Observable<{ linked_equipment: number[] }> {
    return this.api.documents_update_linked_equipment(docId, linkedEquipmentIds);
  }

  public updateDocumentLinkedInvestment(docId: number, linkedInvestmentIds: number[]): Observable<{ linked_investment: number[] }> {
    return this.api.documents_update_linked_investment(docId, linkedInvestmentIds);
  }

  public updateDocumentLinkedInitiative(docId: number, linkedInitiativeIds: number[]): Observable<{ linked_initiative: number[] }> {
    return this.api.documents_update_linked_initiative(docId, linkedInitiativeIds);
  }

  public linkDocumentToEntity(
    docId: number,
    appLabel: string,
    model: string,
    objectId: number,
  ): Observable<any> {
    return this.api.documents_link_to_entity({
      document_id: docId,
      app_label: appLabel,
      model: model,
      object_id: objectId,
    });
  }

  public addDocumentViaRelationship(
    relationshipId: number,
    documentData: Partial<KPI.Document> & { id?: number },
    action: "create" | "update" | "delete" = "create",
  ): Observable<number | null> {
    return this.api.documents_document_type_relationship_action({
      action,
      document_type_relationship: relationshipId,
      document: documentData,
    });
  }
}
